import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, FormControl, InputLabel, Select, MenuItem,
  TextField, Alert, Box, Link as MuiLink
} from '@mui/material';
import { SwapHoriz } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Enrollment, SessionClass, SectionInstance } from '../../types/sessions';
import { enrollmentsApi } from '../../services/sessions/enrollmentsApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';

interface TransferClassDialogProps {
  open: boolean;
  enrollment: Enrollment | null;
  classes: SessionClass[];
  onClose: () => void;
  onTransferred: () => void;
}

type RollMode = 'auto' | 'manual';

const TransferClassDialog: React.FC<TransferClassDialogProps> = ({
  open, enrollment, classes, onClose, onTransferred
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [targetClassId, setTargetClassId] = useState<string>('');
  const [targetSectionId, setTargetSectionId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Roll assignment: 'auto' delegates to the target counter; 'manual' sends
  // an explicit rollNumber. When switching to manual we prefill with the
  // source roll — "keep current roll" is the most common override.
  const [rollMode, setRollMode] = useState<RollMode>('auto');
  const [rollInput, setRollInput] = useState<string>('');
  const [rollError, setRollError] = useState<string | null>(null);
  const rollInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setTargetClassId('');
    setTargetSectionId('');
    setReason('');
    setSections([]);
    setRollMode('auto');
    setRollInput('');
    setRollError(null);
  }, [open]);

  useEffect(() => {
    if (!open || !enrollment || !targetClassId) {
      setSections([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const secs = await sectionInstancesApi.list({
          sessionId: enrollment.sessionId,
          classId: targetClassId
        });
        if (!cancelled) setSections(secs);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, enrollment, targetClassId]);

  const targetClass = useMemo(
    () => classes.find(c => c._id === targetClassId) ?? null,
    [classes, targetClassId]
  );
  const targetSection = sections.find(s => s._id === targetSectionId);

  const sameClass = enrollment ? String(enrollment.classId) === targetClassId : false;
  const sameSection =
    (enrollment?.sectionInstanceId ?? null)?.toString() ===
    (targetSectionId || null)?.toString();
  const sameSlot = sameClass && sameSection;

  const parsedRoll = rollInput.trim() === '' ? null : Number(rollInput);
  const rollIsValid =
    rollMode === 'auto' ||
    (parsedRoll !== null && Number.isInteger(parsedRoll) && parsedRoll >= 1);
  const rollMatchesSource =
    rollMode === 'manual' && parsedRoll !== null && parsedRoll === enrollment?.rollNumber;

  const enableManualOverride = () => {
    setRollMode('manual');
    setRollError(null);
    setRollInput(String(enrollment?.rollNumber ?? ''));
    // Focus + select the prefilled value so admin can Confirm immediately
    // (keep same roll) OR type over it (custom).
    window.requestAnimationFrame(() => {
      rollInputRef.current?.focus();
      rollInputRef.current?.select();
    });
  };

  const disableManualOverride = () => {
    setRollMode('auto');
    setRollInput('');
    setRollError(null);
  };

  const submit = async () => {
    if (!enrollment || !targetClassId) return;
    if (rollMode === 'manual' && !rollIsValid) {
      setRollError('Enter a positive whole number');
      rollInputRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setRollError(null);
    try {
      const result = await enrollmentsApi.transferClass(enrollment._id, {
        targetClassId,
        targetSectionInstanceId: targetSectionId || null,
        rollNumber: rollMode === 'manual' && parsedRoll !== null ? parsedRoll : undefined,
        reason: reason.trim() || undefined
      });
      enqueueSnackbar(
        `Transferred — now Roll ${result.rollNumber} in ${targetClass?.name ?? 'target class'}`,
        { variant: 'success' }
      );
      onTransferred();
      onClose();
    } catch (err: any) {
      const msg: string = err?.message || 'Could not transfer';
      // Backend maps duplicate-key on the roll slot to
      // "Roll number already taken in target slot". Surface inline instead of
      // burying it in a snackbar so admin can correct the field in place.
      if (rollMode === 'manual' && /already taken/i.test(msg)) {
        setRollError(`Roll ${parsedRoll} is already taken in ${targetClass?.name ?? 'the target class'}${targetSection ? ` – ${targetSection.name}` : ''}. Pick another or auto-assign.`);
        rollInputRef.current?.focus();
      } else {
        enqueueSnackbar(msg, { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!enrollment) return null;

  const summaryText = (() => {
    if (!targetClass) return null;
    const slotLabel = <><strong>{targetClass.name}{targetSection ? ` – ${targetSection.name}` : ''}</strong></>;
    if (rollMode === 'auto') {
      return <>Will move to {slotLabel}. A new roll number is auto-assigned in the new slot.</>;
    }
    if (parsedRoll === null) {
      return <>Will move to {slotLabel}. Enter a roll number below.</>;
    }
    if (rollMatchesSource) {
      return <>Will move to {slotLabel} keeping <strong>Roll {parsedRoll}</strong> (same as current).</>;
    }
    return <>Will move to {slotLabel} with <strong>Roll {parsedRoll}</strong>.</>;
  })();

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <SwapHoriz color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Transfer to another class or section</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          This closes the current enrollment as <em>moved within session</em> and creates a new row in the target slot.
          The student's full history shows both rows under the same session.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Target class</InputLabel>
              <Select
                value={targetClassId}
                onChange={(e) => { setTargetClassId(e.target.value); setTargetSectionId(''); setRollError(null); }}
                label="Target class"
              >
                {classes.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              required={sections.length > 0}
              disabled={!targetClassId || sections.length === 0}
            >
              <InputLabel>Target section</InputLabel>
              <Select
                value={targetSectionId}
                onChange={(e) => { setTargetSectionId(e.target.value); setRollError(null); }}
                label="Target section"
              >
                {/* When the target class has sections, admin MUST pick one
                    (matches the BE rule). */}
                {sections.length === 0 ? (
                  <MenuItem value=""><em>No sections in this class</em></MenuItem>
                ) : (
                  <MenuItem value="" disabled><em>Select a section</em></MenuItem>
                )}
                {sections.map(s => (
                  <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Roll assignment — hidden until admin opts in. Prefilling with the
              current roll makes "keep the same number" a one-click action:
              open the field, hit Confirm. */}
          <Box>
            {rollMode === 'auto' ? (
              <Typography variant="caption" color="text.secondary">
                Roll number will be auto-assigned in the new slot.{' '}
                <MuiLink
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={enableManualOverride}
                  sx={{ textDecorationStyle: 'dotted' }}
                >
                  Keep Roll {enrollment.rollNumber} or set manually
                </MuiLink>
              </Typography>
            ) : (
              <TextField
                inputRef={rollInputRef}
                label="Roll number in new slot"
                value={rollInput}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^\d]/g, '');
                  setRollInput(v);
                  if (rollError) setRollError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); submit(); }
                }}
                error={Boolean(rollError)}
                helperText={
                  rollError ? rollError : (
                    <Box component="span">
                      Must be unique in the target class/section. Prefilled with current roll — clear or edit as needed.{' '}
                      <MuiLink
                        component="button"
                        type="button"
                        variant="caption"
                        onClick={disableManualOverride}
                        sx={{ textDecorationStyle: 'dotted' }}
                      >
                        Use auto-assign instead
                      </MuiLink>
                    </Box>
                  )
                }
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                fullWidth
              />
            )}
          </Box>

          <TextField
            label="Reason (optional)"
            placeholder="e.g. Parent request, stream change"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />

          {targetClass && (
            <Alert severity={sameSlot ? 'warning' : 'info'} variant="outlined">
              {sameSlot
                ? 'Target is the same as the current slot — change something to enable transfer.'
                : summaryText}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          disabled={
            submitting ||
            !targetClassId ||
            sameSlot ||
            (rollMode === 'manual' && !rollIsValid) ||
            // Section-required rule — matches the BE guard. Prevents the
            // dialog from firing a request that would 400 anyway.
            (sections.length > 0 && !targetSectionId)
          }
          onClick={submit}
        >
          {submitting ? 'Transferring…' : 'Confirm transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferClassDialog;
